import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { canViewAllData, hasPermission, FEATURE_PERMISSIONS } from '../utils/permissions';
import { collection, query, where, getDocs, writeBatch, doc, setDoc, getDoc, serverTimestamp, Timestamp, orderBy, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';

/**
 * GamificationMaintenancePage - Admin page for managing gamification data cleanup
 * 
 * Features:
 * - Cleanup gamificationCooldowns (delete old records)
 * - Consolidate gamificationDailyCounts (yearly summaries)
 * - Consolidate gamificationEntityAwards (yearly summaries)
 * - Dry-run mode to preview changes
 * - Audit logging of all operations
 * 
 * Can be used standalone via route or embedded in AchievementManagementPage as a tab.
 * Pass embedded={true} to hide the back button and outer page wrapper.
 */
const GamificationMaintenancePage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dryRunMode, setDryRunMode] = useState(true);
  const [lastRunStats, setLastRunStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  
  // Cleanup thresholds
  const [cooldownDays, setCooldownDays] = useState(14); // Delete cooldowns older than X days
  const [consolidationYear, setConsolidationYear] = useState(new Date().getFullYear() - 1);
  
  // Preview results
  const [previewResults, setPreviewResults] = useState(null);
  
  // Load recent maintenance logs
  useEffect(() => {
    if (!canViewAllData(currentUser)) return;
    
    const loadRecentLogs = async () => {
      setLoadingLogs(true);
      try {
        const q = query(
          collection(db, collectionPath('gamificationMaintenanceLogs')),
          orderBy('runAt', 'desc'),
          limit(10)
        );
        const snapshot = await getDocs(q);
        const logs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setRecentLogs(logs);
      } catch (err) {
        console.error('Failed to load maintenance logs:', err);
      } finally {
        setLoadingLogs(false);
      }
    };
    loadRecentLogs();
  }, [lastRunStats, currentUser?.role]);

  // Check admin access - must be after hooks
  if (!canViewAllData(currentUser)) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold">Access Denied</p>
          <p className="text-red-600 text-sm mt-1">
            This page is admin-only. You don't have permission to access gamification maintenance.
          </p>
        </div>
      </main>
    );
  }

  /**
   * Preview what would be cleaned up (dry run)
   */
  const runPreview = async () => {
    setLoading(true);
    setPreviewResults(null);
    
    try {
      const results = {
        cooldowns: { count: 0, oldestDate: null },
        dailyCounts: { count: 0, users: new Set(), actions: new Set() },
        entityAwards: { count: 0, users: new Set(), actions: new Set() }
      };
      
      // 1. Preview cooldowns cleanup
      const cooldownCutoff = new Date();
      cooldownCutoff.setDate(cooldownCutoff.getDate() - cooldownDays);
      
      const cooldownsQuery = query(
        collection(db, collectionPath('gamificationCooldowns')),
        where('lastAction', '<', Timestamp.fromDate(cooldownCutoff))
      );
      const cooldownsSnap = await getDocs(cooldownsQuery);
      results.cooldowns.count = cooldownsSnap.size;
      
      if (cooldownsSnap.size > 0) {
        const dates = cooldownsSnap.docs.map(d => d.data().lastAction?.toDate?.() || new Date(0));
        results.cooldowns.oldestDate = new Date(Math.min(...dates.map(d => d.getTime())));
      }
      
      // 2. Preview dailyCounts consolidation
      const yearStart = `${consolidationYear}-01-01`;
      const yearEnd = `${consolidationYear}-12-31`;
      
      const dailyCountsQuery = query(
        collection(db, collectionPath('gamificationDailyCounts')),
        where('date', '>=', yearStart),
        where('date', '<=', yearEnd)
      );
      const dailyCountsSnap = await getDocs(dailyCountsQuery);
      results.dailyCounts.count = dailyCountsSnap.size;
      
      dailyCountsSnap.docs.forEach(d => {
        const data = d.data();
        results.dailyCounts.users.add(data.userId);
        results.dailyCounts.actions.add(data.action);
      });
      
      // 3. Preview entityAwards consolidation
      const entityAwardsQuery = query(
        collection(db, collectionPath('gamificationEntityAwards')),
        where('awardedAt', '>=', Timestamp.fromDate(new Date(`${consolidationYear}-01-01`))),
        where('awardedAt', '<=', Timestamp.fromDate(new Date(`${consolidationYear}-12-31T23:59:59`)))
      );
      const entityAwardsSnap = await getDocs(entityAwardsQuery);
      results.entityAwards.count = entityAwardsSnap.size;
      
      entityAwardsSnap.docs.forEach(d => {
        const data = d.data();
        results.entityAwards.users.add(data.userId);
        results.entityAwards.actions.add(data.action);
      });
      
      // Convert Sets to counts for display
      results.dailyCounts.userCount = results.dailyCounts.users.size;
      results.dailyCounts.actionCount = results.dailyCounts.actions.size;
      results.entityAwards.userCount = results.entityAwards.users.size;
      results.entityAwards.actionCount = results.entityAwards.actions.size;
      
      // Clean up Sets before setting state
      delete results.dailyCounts.users;
      delete results.dailyCounts.actions;
      delete results.entityAwards.users;
      delete results.entityAwards.actions;
      
      setPreviewResults(results);
      setToast({ message: 'Preview complete! Review the results below.', type: 'success' });
      
    } catch (err) {
      console.error('Preview failed:', err);
      setToast({ message: `Preview failed: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Execute cooldowns cleanup
   */
  const runCooldownsCleanup = async () => {
    if (dryRunMode) {
      setToast({ message: 'Dry-run mode is ON. Turn it off to execute cleanup.', type: 'warning' });
      return;
    }
    
    if (!window.confirm(`⚠️ This will permanently delete cooldown records older than ${cooldownDays} days.\n\nAre you sure?`)) {
      return;
    }
    
    setLoading(true);
    
    try {
      const cooldownCutoff = new Date();
      cooldownCutoff.setDate(cooldownCutoff.getDate() - cooldownDays);
      
      const cooldownsQuery = query(
        collection(db, collectionPath('gamificationCooldowns')),
        where('lastAction', '<', Timestamp.fromDate(cooldownCutoff))
      );
      const cooldownsSnap = await getDocs(cooldownsQuery);
      
      if (cooldownsSnap.empty) {
        setToast({ message: 'No cooldown records found to clean up.', type: 'info' });
        setLoading(false);
        return;
      }
      
      // Delete in batches of 500
      const batchSize = 500;
      let deleted = 0;
      let batch = writeBatch(db);
      let batchCount = 0;
      
      for (const docSnap of cooldownsSnap.docs) {
        batch.delete(docSnap.ref);
        batchCount++;
        
        if (batchCount >= batchSize) {
          await batch.commit();
          deleted += batchCount;
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      
      // Commit remaining
      if (batchCount > 0) {
        await batch.commit();
        deleted += batchCount;
      }
      
      // Log the cleanup
      const logId = `cleanup_cooldowns_${Date.now()}`;
      await setDoc(doc(db, collectionPath('gamificationMaintenanceLogs'), logId), {
        type: 'cooldowns_cleanup',
        runAt: serverTimestamp(),
        runBy: currentUser.uid,
        runByEmail: currentUser.email,
        parameters: { cooldownDays },
        results: {
          deletedCount: deleted,
          cutoffDate: cooldownCutoff.toISOString()
        },
        automatic: false
      });
      
      setLastRunStats({ type: 'cooldowns', deleted });
      setToast({ message: `✅ Successfully deleted ${deleted} cooldown records.`, type: 'success' });
      
    } catch (err) {
      console.error('Cooldowns cleanup failed:', err);
      setToast({ message: `Cleanup failed: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Execute yearly consolidation for dailyCounts and entityAwards
   */
  const runYearlyConsolidation = async () => {
    if (dryRunMode) {
      setToast({ message: 'Dry-run mode is ON. Turn it off to execute consolidation.', type: 'warning' });
      return;
    }
    
    const currentYear = new Date().getFullYear();
    if (consolidationYear > currentYear) {
      setToast({ message: `Cannot consolidate future years. Select ${currentYear} or earlier.`, type: 'error' });
      return;
    }
    
    const isCurrentYear = consolidationYear === currentYear;
    const confirmMessage = isCurrentYear
      ? `⚠️ This will consolidate all ${consolidationYear} gamification data (year in progress) into yearly summaries.\n\n` +
        `New records added after this run will be MERGED with the existing summary on subsequent runs.\n\n` +
        `Original records will be deleted after consolidation.\n\n` +
        `Continue?`
      : `⚠️ This will consolidate all gamification data for ${consolidationYear} into yearly summaries.\n\n` +
        `If a summary already exists, new records will be MERGED with existing totals.\n\n` +
        `Original records will be deleted after consolidation.\n\n` +
        `Continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setLoading(true);
    
    try {
      const results = {
        dailyCountsProcessed: 0,
        entityAwardsProcessed: 0,
        summariesCreated: 0,
        usersProcessed: new Set()
      };
      
      // ===== STEP 1: Consolidate Daily Counts =====
      const yearStart = `${consolidationYear}-01-01`;
      const yearEnd = `${consolidationYear}-12-31`;
      
      const dailyCountsQuery = query(
        collection(db, collectionPath('gamificationDailyCounts')),
        where('date', '>=', yearStart),
        where('date', '<=', yearEnd)
      );
      const dailyCountsSnap = await getDocs(dailyCountsQuery);
      
      // Group by userId
      const userDailyCounts = {};
      dailyCountsSnap.docs.forEach(d => {
        const data = d.data();
        if (!userDailyCounts[data.userId]) {
          userDailyCounts[data.userId] = {
            actionCounts: {},
            totalActions: 0,
            dailyRecordIds: []
          };
        }
        
        const actionKey = data.action;
        if (!userDailyCounts[data.userId].actionCounts[actionKey]) {
          userDailyCounts[data.userId].actionCounts[actionKey] = 0;
        }
        userDailyCounts[data.userId].actionCounts[actionKey] += data.count || 0;
        userDailyCounts[data.userId].totalActions += data.count || 0;
        userDailyCounts[data.userId].dailyRecordIds.push(d.id);
        
        results.usersProcessed.add(data.userId);
      });
      
      results.dailyCountsProcessed = dailyCountsSnap.size;
      
      // ===== STEP 2: Consolidate Entity Awards =====
      const entityAwardsQuery = query(
        collection(db, collectionPath('gamificationEntityAwards')),
        where('awardedAt', '>=', Timestamp.fromDate(new Date(`${consolidationYear}-01-01`))),
        where('awardedAt', '<=', Timestamp.fromDate(new Date(`${consolidationYear}-12-31T23:59:59`)))
      );
      const entityAwardsSnap = await getDocs(entityAwardsQuery);
      
      // Group by userId
      const userEntityAwards = {};
      entityAwardsSnap.docs.forEach(d => {
        const data = d.data();
        if (!userEntityAwards[data.userId]) {
          userEntityAwards[data.userId] = {
            awardsByAction: {},
            totalAwards: 0,
            awardRecordIds: []
          };
        }
        
        const actionKey = data.action;
        if (!userEntityAwards[data.userId].awardsByAction[actionKey]) {
          userEntityAwards[data.userId].awardsByAction[actionKey] = {
            count: 0,
            entities: []
          };
        }
        userEntityAwards[data.userId].awardsByAction[actionKey].count++;
        userEntityAwards[data.userId].awardsByAction[actionKey].entities.push({
          entityId: data.entityId,
          stageOrVariant: data.stageOrVariant || null
        });
        userEntityAwards[data.userId].totalAwards++;
        userEntityAwards[data.userId].awardRecordIds.push(d.id);
        
        results.usersProcessed.add(data.userId);
      });
      
      results.entityAwardsProcessed = entityAwardsSnap.size;
      
      // ===== STEP 3: Create/Merge Yearly Summaries & Delete Original Records =====
      const allUserIds = [...results.usersProcessed];
      let summariesMerged = 0;
      
      for (const userId of allUserIds) {
        const summaryId = `${userId}_${consolidationYear}`;
        const summaryRef = doc(db, collectionPath('gamificationYearlySummary'), summaryId);
        
        // Fetch existing summary if it exists
        const existingSnap = await getDoc(summaryRef);
        const existingSummary = existingSnap.exists() ? existingSnap.data() : null;
        
        // Merge daily counts with existing summary
        let mergedDailyCounts = null;
        if (userDailyCounts[userId] || existingSummary?.dailyCounts) {
          const existingActionCounts = existingSummary?.dailyCounts?.actionCounts || {};
          const newActionCounts = userDailyCounts[userId]?.actionCounts || {};
          
          // Merge action counts (add values together)
          const mergedActionCounts = { ...existingActionCounts };
          for (const [action, count] of Object.entries(newActionCounts)) {
            mergedActionCounts[action] = (mergedActionCounts[action] || 0) + count;
          }
          
          mergedDailyCounts = {
            actionCounts: mergedActionCounts,
            totalActions: (existingSummary?.dailyCounts?.totalActions || 0) + (userDailyCounts[userId]?.totalActions || 0),
            originalRecordCount: (existingSummary?.dailyCounts?.originalRecordCount || 0) + (userDailyCounts[userId]?.dailyRecordIds?.length || 0)
          };
        }
        
        // Merge entity awards with existing summary
        let mergedEntityAwards = null;
        if (userEntityAwards[userId] || existingSummary?.entityAwards) {
          const existingAwardsByAction = existingSummary?.entityAwards?.awardsByAction || {};
          const newAwardsByAction = userEntityAwards[userId]?.awardsByAction || {};
          
          // Merge awards by action (add counts and concatenate entity lists)
          const mergedAwardsByAction = { ...existingAwardsByAction };
          for (const [action, data] of Object.entries(newAwardsByAction)) {
            if (!mergedAwardsByAction[action]) {
              mergedAwardsByAction[action] = { count: 0, entities: [] };
            }
            mergedAwardsByAction[action].count += data.count;
            mergedAwardsByAction[action].entities = [
              ...(mergedAwardsByAction[action].entities || []),
              ...data.entities
            ];
          }
          
          mergedEntityAwards = {
            awardsByAction: mergedAwardsByAction,
            totalAwards: (existingSummary?.entityAwards?.totalAwards || 0) + (userEntityAwards[userId]?.totalAwards || 0),
            originalRecordCount: (existingSummary?.entityAwards?.originalRecordCount || 0) + (userEntityAwards[userId]?.awardRecordIds?.length || 0)
          };
        }
        
        // Build merged summary document
        const summary = {
          userId,
          year: consolidationYear,
          createdAt: existingSummary?.createdAt || serverTimestamp(),
          createdBy: existingSummary?.createdBy || currentUser.uid,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
          mergeCount: (existingSummary?.mergeCount || 0) + 1,
          
          // Merged daily counts summary
          dailyCounts: mergedDailyCounts,
          
          // Merged entity awards summary
          entityAwards: mergedEntityAwards
        };
        
        // Save merged summary
        await setDoc(summaryRef, summary);
        if (existingSummary) {
          summariesMerged++;
        }
        results.summariesCreated++;
        
        // Delete original daily counts records in batches
        if (userDailyCounts[userId]) {
          const dailyRecordIds = userDailyCounts[userId].dailyRecordIds;
          for (let i = 0; i < dailyRecordIds.length; i += 500) {
            const batch = writeBatch(db);
            const chunk = dailyRecordIds.slice(i, i + 500);
            chunk.forEach(id => {
              batch.delete(doc(db, collectionPath('gamificationDailyCounts'), id));
            });
            await batch.commit();
          }
        }
        
        // Delete original entity awards records in batches
        if (userEntityAwards[userId]) {
          const awardRecordIds = userEntityAwards[userId].awardRecordIds;
          for (let i = 0; i < awardRecordIds.length; i += 500) {
            const batch = writeBatch(db);
            const chunk = awardRecordIds.slice(i, i + 500);
            chunk.forEach(id => {
              batch.delete(doc(db, collectionPath('gamificationEntityAwards'), id));
            });
            await batch.commit();
          }
        }
      }
      
      // ===== STEP 4: Log the consolidation =====
      const logId = `consolidation_${consolidationYear}_${Date.now()}`;
      await setDoc(doc(db, collectionPath('gamificationMaintenanceLogs'), logId), {
        type: 'yearly_consolidation',
        runAt: serverTimestamp(),
        runBy: currentUser.uid,
        runByEmail: currentUser.email,
        parameters: { year: consolidationYear },
        results: {
          dailyCountsProcessed: results.dailyCountsProcessed,
          entityAwardsProcessed: results.entityAwardsProcessed,
          summariesCreated: results.summariesCreated,
          summariesMerged,
          usersProcessed: allUserIds.length
        },
        automatic: false
      });
      
      setLastRunStats({
        type: 'consolidation',
        year: consolidationYear,
        dailyCountsProcessed: results.dailyCountsProcessed,
        entityAwardsProcessed: results.entityAwardsProcessed,
        summariesCreated: results.summariesCreated,
        summariesMerged,
        usersProcessed: allUserIds.length
      });
      
      const mergeNote = summariesMerged > 0 ? ` (${summariesMerged} merged with existing)` : '';
      setToast({ 
        message: `✅ Successfully consolidated ${consolidationYear} data: ${results.summariesCreated} user summaries${mergeNote}.`, 
        type: 'success' 
      });
      
    } catch (err) {
      console.error('Consolidation failed:', err);
      setToast({ message: `Consolidation failed: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Wrapper component or just content based on embedded mode
  const content = (
    <>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      {!embedded && (
        <div className="mb-6">
          <button 
            onClick={() => navigate('/admin')}
            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 mb-4"
          >
            ← Back to Admin
          </button>
          <h2 className="text-2xl font-bold text-gray-900">🎮 Gamification Maintenance</h2>
          <p className="text-gray-600 mt-1">
            Clean up old gamification data and consolidate historical records into yearly summaries.
          </p>
        </div>
      )}

      {/* Info Panel - About Gamification Data */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">ℹ️ About Gamification Data</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li><strong>Cooldowns</strong>: Track action timestamps to prevent point farming. Safe to delete after 14+ days.</li>
          <li><strong>Daily Counts</strong>: Track daily action limits per user. Consolidated into yearly summaries.</li>
          <li><strong>Entity Awards</strong>: Prevent duplicate awards for the same entity. Consolidated into yearly summaries.</li>
          <li><strong>Yearly Summaries</strong>: Stored in <code className="bg-blue-100 px-1 rounded">gamificationYearlySummary</code> for historical reference.</li>
          <li><strong>Automatic Cleanup</strong>: Runs daily at 2 AM via Firebase Cloud Function.</li>
        </ul>
      </div>

      {/* Dry Run Mode Toggle */}
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🧪</span>
            <div>
              <h3 className="font-semibold text-amber-900">Dry-Run Mode</h3>
              <p className="text-sm text-amber-700">
                {dryRunMode 
                  ? 'Enabled - Operations will only preview changes without modifying data' 
                  : 'Disabled - Operations will permanently modify data'}
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={dryRunMode}
              onChange={(e) => setDryRunMode(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cooldowns Log Cleanup Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-blue-500">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">⏱️</span>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Cooldowns Log Cleanup</h3>
              <p className="text-sm text-gray-600">Delete expired cooldown tracking records</p>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delete records older than (days):
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={cooldownDays}
              onChange={(e) => setCooldownDays(parseInt(e.target.value) || 7)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Cooldowns prevent spam actions. Old records (14+ days) are safe to delete.
            </p>
          </div>
          
          <div className="flex gap-2">
            <Tooltip text="Preview what would be deleted without making changes">
              <button
                onClick={runPreview}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium"
              >
                👁️ Preview
              </button>
            </Tooltip>
            <Tooltip text={dryRunMode ? "Turn off dry-run mode to execute" : "Permanently delete old cooldown records"}>
              <button
                onClick={runCooldownsCleanup}
                disabled={loading || dryRunMode || !hasPermission(currentUser, FEATURE_PERMISSIONS.ACHIEVEMENTS_MANAGE)}
                className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                  dryRunMode || !hasPermission(currentUser, FEATURE_PERMISSIONS.ACHIEVEMENTS_MANAGE)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:opacity-50`}
              >
                🗑️ Clean Up
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Yearly Log Consolidation Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-purple-500">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">📊</span>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Yearly Log Consolidation</h3>
              <p className="text-sm text-gray-600">Consolidate daily counts & entity awards into summaries</p>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Consolidate year:
            </label>
            <select
              value={consolidationYear}
              onChange={(e) => setConsolidationYear(parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>
                  {year}{year === new Date().getFullYear() ? ' (current)' : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Creates/merges user summaries in <code className="bg-gray-100 px-1 rounded">gamificationYearlySummary</code> and deletes processed records.
            </p>
          </div>
          
          <div className="flex gap-2">
            <Tooltip text="Preview records that would be consolidated">
              <button
                onClick={runPreview}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium"
              >
                👁️ Preview
              </button>
            </Tooltip>
            <Tooltip text={dryRunMode ? "Turn off dry-run mode to execute" : "Consolidate and delete original records"}>
              <button
                onClick={runYearlyConsolidation}
                disabled={loading || dryRunMode || !hasPermission(currentUser, FEATURE_PERMISSIONS.ACHIEVEMENTS_MANAGE)}
                className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                  dryRunMode || !hasPermission(currentUser, FEATURE_PERMISSIONS.ACHIEVEMENTS_MANAGE)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                } disabled:opacity-50`}
              >
                📦 Consolidate
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Preview Results */}
      {previewResults && (
        <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📋 Preview Results</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Cooldowns Preview */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">⏱️ Cooldowns</h4>
              <div className="text-3xl font-bold text-blue-600">{previewResults.cooldowns.count}</div>
              <p className="text-sm text-blue-700">records to delete</p>
              {previewResults.cooldowns.oldestDate && (
                <p className="text-xs text-blue-600 mt-2">
                  Oldest: {previewResults.cooldowns.oldestDate.toLocaleDateString()}
                </p>
              )}
            </div>
            
            {/* Daily Counts Preview */}
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">📅 Daily Counts ({consolidationYear})</h4>
              <div className="text-3xl font-bold text-green-600">{previewResults.dailyCounts.count}</div>
              <p className="text-sm text-green-700">records to consolidate</p>
              <p className="text-xs text-green-600 mt-2">
                {previewResults.dailyCounts.userCount} users, {previewResults.dailyCounts.actionCount} action types
              </p>
            </div>
            
            {/* Entity Awards Preview */}
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2">🏆 Entity Awards ({consolidationYear})</h4>
              <div className="text-3xl font-bold text-purple-600">{previewResults.entityAwards.count}</div>
              <p className="text-sm text-purple-700">records to consolidate</p>
              <p className="text-xs text-purple-600 mt-2">
                {previewResults.entityAwards.userCount} users, {previewResults.entityAwards.actionCount} action types
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Last Run Stats */}
      {lastRunStats && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-semibold text-green-900 mb-2">✅ Last Operation Completed</h3>
          {lastRunStats.type === 'cooldowns' && (
            <p className="text-green-700">
              Deleted <strong>{lastRunStats.deleted}</strong> cooldown records.
            </p>
          )}
          {lastRunStats.type === 'consolidation' && (
            <p className="text-green-700">
              Consolidated <strong>{lastRunStats.year}</strong> data: {lastRunStats.summariesCreated} user summaries created 
              ({lastRunStats.dailyCountsProcessed} daily counts, {lastRunStats.entityAwardsProcessed} entity awards processed).
            </p>
          )}
        </div>
      )}

      {/* Recent Maintenance Logs */}
      <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📜 Recent Maintenance Logs</h3>
        
        {loadingLogs ? (
          <div className="text-center py-4 text-gray-500">Loading logs...</div>
        ) : recentLogs.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No maintenance logs yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Run At</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Run By</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Results</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Source</th>
                </tr>
              </thead>
              <tbody>
                {recentLogs.map(log => (
                  <tr key={log.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        log.type === 'cooldowns_cleanup' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {log.type === 'cooldowns_cleanup' ? '⏱️ Cooldowns' : '📦 Consolidation'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(log.runAt)}</td>
                    <td className="px-4 py-3 text-gray-600">{log.runByEmail || log.runBy || 'System'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {log.type === 'cooldowns_cleanup' && (
                        <span>{log.results?.deletedCount || 0} deleted</span>
                      )}
                      {log.type === 'yearly_consolidation' && (
                        <span>
                          {log.results?.summariesCreated || 0} summaries, 
                          {' '}{log.results?.usersProcessed || 0} users
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        log.automatic ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
                      }`}>
                        {log.automatic ? '🤖 Auto' : '👤 Manual'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  // Return wrapper or embedded content
  if (embedded) {
    return content;
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      {content}
    </main>
  );
};

export default GamificationMaintenancePage;
